import type {
  ColumnMapping,
  ImportDefaults,
  ImportDraft,
  ParsedFile,
  RawRow,
} from './types';

/**
 * Resolve a party's currency from its country per the agreed rule
 * (IE → EUR, GB / XI → GBP), or the fixed code when the operator chose a
 * fixed currency. Unknown countries fall back to EUR.
 */
export function resolveCurrency(country: string, defaults: ImportDefaults): string {
  if (defaults.currencyMode === 'fixed') return defaults.fixedCurrency.toUpperCase() || 'EUR';
  const c = country.trim().toUpperCase();
  if (c === 'GB' || c === 'XI') return 'GBP';
  return 'EUR'; // IE + fallback
}

/** Parse a numeric cell → number or null (blank / non-finite → null). */
function toNum(v: string | undefined): number | null {
  if (v == null) return null;
  const s = v.trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Pull a mapped value from a raw row (trimmed), or '' when unmapped/blank. */
function val(row: RawRow, mapping: ColumnMapping, field: keyof ColumnMapping): string {
  const header = mapping[field];
  if (!header) return '';
  return (row[header] ?? '').trim();
}

/**
 * Materialise the parsed rows into editable drafts by applying the column
 * mapping + batch defaults + light coercion. This is the bridge from the
 * mapping step to the preview grid — drafts are what the operator edits and
 * what the commit step round-trips.
 *
 * Fallbacks: billingName / addrName default to name; country is uppercased;
 * currency is resolved from country (or fixed); placeId starts empty (the
 * geocode step fills it).
 */
export function buildDrafts(
  parsed: ParsedFile,
  mapping: ColumnMapping,
  defaults: ImportDefaults,
): ImportDraft[] {
  return parsed.rows.map((row, i) => {
    const name = val(row, mapping, 'name');
    const billingName = val(row, mapping, 'billingName') || name;
    const addrName = val(row, mapping, 'addrName') || name;
    const country = val(row, mapping, 'country').toUpperCase();

    return {
      rowRef: `r${i}`,
      code: val(row, mapping, 'code'),
      name,
      billingName,
      type: defaults.type,
      subtype: null, // supplier subtype mapping is v2
      taxNumber: val(row, mapping, 'taxNumber'),
      tags: val(row, mapping, 'tags'),
      status: defaults.status,
      paymentMethod: defaults.paymentMethod,
      billingFrequency: defaults.billingFrequency,
      currencyIsoCode: resolveCurrency(country, defaults),
      addrName,
      fullAddress: val(row, mapping, 'fullAddress'),
      city: val(row, mapping, 'city'),
      postcode: val(row, mapping, 'postcode'),
      country,
      latitude: toNum(mapping.latitude ? row[mapping.latitude] : undefined),
      longitude: toNum(mapping.longitude ? row[mapping.longitude] : undefined),
      placeId: '',
      status_: 'pending',
    };
  });
}
